import { gql, type ApolloClient, type TypedDocumentNode } from "@apollo/client";
import { z } from "zod";
import { RestartGameSessionDocument, StartGameSessionDocument } from "@/graphql/generated/operations";
import { ELEMENT_CODES } from "@/lib/elements";
import { graphQlErrorExtensions } from "@/lib/graphql-error";
import {
  GAME_PAUSED, ROLEPLAY_GAME_NOT_FOUND, ROLEPLAY_GAME_NOT_OPEN,
  ROLEPLAY_POWERS_LOCKED, ROLEPLAY_POWERS_INVALID,
} from "@/lib/genesis-error-codes";
import { isUuid, RoleplayError, UUID_RE } from "./contract";

// These roleplay fields are on the Genesis game branch. Keep the documents local until the generated schema includes them.
export const ROLEPLAY_GAME_KEY = "roleplay.window-seat";
const knownPower = z.string().refine((value) => Object.hasOwn(ELEMENT_CODES, value));
const distinct = (powers: string[]) => new Set(powers).size === powers.length;
const chosenPowers = z.array(knownPower).min(1).max(5).refine(distinct);

const gameSession = z.object({ id: z.string().regex(UUID_RE), gameKey: z.literal(ROLEPLAY_GAME_KEY) });
// genesis deletes the chosen powers of an abandoned or expired session and answers `powers: []` for it (schema
// `RoleplaySession.powers`, @external-adr GENESIS-0093-a-roleplay-session-is-a-measurement-record-its-story-is-not §6-7):
// only a running session must hold one to five.
const roleplaySession = z.object({
  session: gameSession.extend({ status: z.enum(["IN_PROGRESS", "COMPLETED", "ABANDONED", "EXPIRED"]) }),
  powers: z.array(knownPower).max(5).refine(distinct),
  powersLocked: z.boolean(),
  thresholdPercent: z.number().int().min(1).max(100),
}).refine((value) => value.session.status !== "IN_PROGRESS" || value.powers.length > 0);
export type GenesisRoleplaySession = z.output<typeof roleplaySession>;

const FIELDS = gql`
  fragment RoleplayChoiceFields on RoleplaySession {
    session { id gameKey status }
    powers
    powersLocked
    thresholdPercent
  }
`;

export const MY_ROLEPLAY_SESSION: TypedDocumentNode<{ myRoleplaySession: unknown }, { id: string }> = gql`
  query MyRoleplaySession($id: UUID!) {
    myRoleplaySession(id: $id) { ...RoleplayChoiceFields }
  }
  ${FIELDS}
`;
export const SET_ROLEPLAY_SESSION_POWERS: TypedDocumentNode<
  { setRoleplaySessionPowers: unknown }, { input: { id: string; powers: string[] } }
> = gql`
  mutation SetRoleplaySessionPowers($input: RoleplaySessionPowersInput!) {
    setRoleplaySessionPowers(input: $input) { ...RoleplayChoiceFields }
  }
  ${FIELDS}
`;
export const LOCK_ROLEPLAY_SESSION_POWERS: TypedDocumentNode<{ lockRoleplaySessionPowers: unknown }, { id: string }> = gql`
  mutation LockRoleplaySessionPowers($id: UUID!) {
    lockRoleplaySessionPowers(id: $id) { ...RoleplayChoiceFields }
  }
  ${FIELDS}
`;

function sessionId(id: string): string {
  if (!isUuid(id)) throw new RoleplayError("ROLEPLAY_INVALID_INPUT");
  return id;
}

function sessionOf(value: unknown, id: string): GenesisRoleplaySession {
  const parsed = roleplaySession.safeParse(value);
  if (!parsed.success || parsed.data.session.id.toLowerCase() !== id.toLowerCase()) throw new RoleplayError("UNKNOWN");
  return parsed.data;
}

async function head(client: ApolloClient, restart: boolean): Promise<string> {
  const raw = restart
    ? (await client.mutate({ mutation: RestartGameSessionDocument, variables: { gameKey: ROLEPLAY_GAME_KEY }, fetchPolicy: "no-cache" })).data?.restartGameSession
    : (await client.mutate({ mutation: StartGameSessionDocument, variables: { gameKey: ROLEPLAY_GAME_KEY }, fetchPolicy: "no-cache" })).data?.startGameSession;
  const parsed = gameSession.safeParse(raw);
  if (!parsed.success) throw new RoleplayError("UNKNOWN");
  return parsed.data.id;
}

/** Start the plane game: Genesis returns the open session with the Powers it holds, or a new one with five it drew. */
export async function startRoleplaySession(client: ApolloClient): Promise<GenesisRoleplaySession> {
  const id = await head(client, false);
  const session = await readRoleplaySession(client, id);
  if (!session) throw new RoleplayError("UNKNOWN");
  return session;
}

/**
 * Restart the game: Genesis abandons the open session, deletes its readings and chosen Powers, and starts a new one
 * with five Powers drawn anew at random, not locked, like a new game (spark#1075, Entscheidung 127.49).
 */
export async function restartRoleplaySession(client: ApolloClient): Promise<GenesisRoleplaySession> {
  const id = await head(client, true);
  const session = await readRoleplaySession(client, id);
  if (!session) throw new RoleplayError("UNKNOWN");
  return session;
}

export async function readRoleplaySession(client: ApolloClient, id: string): Promise<GenesisRoleplaySession | null> {
  const { data } = await client.query({ query: MY_ROLEPLAY_SESSION, variables: { id: sessionId(id) }, fetchPolicy: "no-cache" });
  return data?.myRoleplaySession == null ? null : sessionOf(data.myRoleplaySession, id);
}

export async function setRoleplaySessionPowers(client: ApolloClient, id: string, powers: string[]): Promise<GenesisRoleplaySession> {
  if (!chosenPowers.safeParse(powers).success) throw new RoleplayError("ROLEPLAY_INVALID_INPUT");
  const { data } = await client.mutate({
    mutation: SET_ROLEPLAY_SESSION_POWERS,
    variables: { input: { id: sessionId(id), powers } },
    fetchPolicy: "no-cache",
  });
  return sessionOf(data?.setRoleplaySessionPowers, id);
}

export async function lockRoleplaySessionPowers(client: ApolloClient, id: string): Promise<GenesisRoleplaySession> {
  const { data } = await client.mutate({
    mutation: LOCK_ROLEPLAY_SESSION_POWERS,
    variables: { id: sessionId(id) },
    fetchPolicy: "no-cache",
  });
  return sessionOf(data?.lockRoleplaySessionPowers, id);
}

export type GenesisRoleplayRefusal = "not_found" | "not_open" | "suspended" | "powers_locked" | "powers_invalid";

export function genesisRoleplayRefusal(error: unknown): GenesisRoleplayRefusal | null {
  if (graphQlErrorExtensions(error, ROLEPLAY_GAME_NOT_FOUND)) return "not_found";
  if (graphQlErrorExtensions(error, ROLEPLAY_GAME_NOT_OPEN)) return "not_open";
  if (graphQlErrorExtensions(error, GAME_PAUSED)) return "suspended";
  if (graphQlErrorExtensions(error, ROLEPLAY_POWERS_LOCKED)) return "powers_locked";
  if (graphQlErrorExtensions(error, ROLEPLAY_POWERS_INVALID)) return "powers_invalid";
  return null;
}
